import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const TestimonialsSection = styled.section`
  padding: 7.2rem 5%;
  background-color: ${props => props.theme.colors.testimonialBackground};
  position: relative;
`;

const SectionTitle = styled.h2`
  font-size: 2.25rem;
  text-align: center;
  margin-bottom: 0.9rem;
  position: relative;
  z-index: 1;
  color: ${props => props.theme.colors.text};
  
  &::after {
    content: '';
    display: block;
    width: 72px;
    height: 3.6px;
    background: ${props => props.theme.colors.accentGradient};
    margin: 0.9rem auto;
    border-radius: 1.8px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 630px;
  margin: 0 auto 3.6rem;
  color: ${props => props.theme.colors.secondaryText};
  position: relative;
  z-index: 1;
  line-height: 1.6;
`;

const TestimonialsContainer = styled.div`
  max-width: 810px;
  margin: 0 auto;
  position: relative;
`;

// Utiliser role="region" avec aria-roledescription="carousel" pour indiquer un carrousel
const TestimonialCarousel = styled.div`
  position: relative;
`;

const TestimonialCard = styled.div`
  background: ${props => props.theme.colors.cardBackground};
  border-radius: 13.5px;
  padding: 2.7rem;
  text-align: center;
  border: 1px solid ${props => props.theme.colors.borderColor};
  backdrop-filter: blur(9px);
  max-width: 720px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease-out;
  box-shadow: 0 13.5px 27px rgba(0, 0, 0, ${props => props.theme.name === 'light' ? '0.05' : '0.2'});
  
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const QuoteIcon = styled.div`
  font-size: 2.7rem;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 1.35rem;
  opacity: 0.5;
`;

const TestimonialText = styled.blockquote`
  font-size: 1.08rem;
  line-height: 1.8;
  margin: 0 0 1.8rem 0;
  font-style: italic;
  color: ${props => props.theme.colors.text};
  
  &::before {
    content: open-quote;
  }
  
  &::after {
    content: close-quote;
  }
`;

const ClientInfo = styled.footer`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ClientImage = styled.img`
  width: 54px;
  height: 54px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 0.9rem;
  border: 2.7px solid ${props => props.theme.colors.accent};
`;

const ClientDetails = styled.div`
  text-align: left;
`;

const ClientName = styled.cite`
  display: block;
  font-size: 0.99rem;
  margin-bottom: 0.18rem;
  color: ${props => props.theme.colors.text};
  font-style: normal;
  font-weight: bold;
`;

const ClientPosition = styled.p`
  font-size: 0.81rem;
  color: ${props => props.theme.colors.secondaryText};
  margin: 0;
`;

const TestimonialControls = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.8rem;
  gap: 0.9rem;
`;

const ControlButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.accent};
  font-size: 1.35rem;
  cursor: pointer;
  padding: 0.45rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:focus {
    outline: 1.8px solid ${props => props.theme.colors.accent};
    outline-offset: 1.8px;
  }
  
  &:disabled {
    color: ${props => props.theme.colors.secondaryText};
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TestimonialDots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 0.9rem;
`;

const Dot = styled.button`
  width: 10.8px;
  height: 10.8px;
  border-radius: 50%;
  background: ${props => props.active ? props.theme.colors.accent : 'rgba(150, 150, 150, 0.2)'};
  margin: 0 0.45rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  padding: 0;
  
  &:hover {
    background: rgba(94, 23, 235, 0.5);
  }
  
  &:focus {
    outline: 1.8px solid ${props => props.theme.colors.accent};
    outline-offset: 1.8px;
  }
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// SVG icons avec attributs d'accessibilité
const QuoteIconSvg = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 512 512"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M464 256h-80v-64c0-35.3 28.7-64 64-64h8c13.3 0 24-10.7 24-24V56c0-13.3-10.7-24-24-24h-8c-88.4 0-160 71.6-160 160v240c0 26.5 21.5 48 48 48h128c26.5 0 48-21.5 48-48V304c0-26.5-21.5-48-48-48zm-288 0H96v-64c0-35.3 28.7-64 64-64h8c13.3 0 24-10.7 24-24V56c0-13.3-10.7-24-24-24h-8C71.6 32 0 103.6 0 192v240c0 26.5 21.5 48 48 48h128c26.5 0 48-21.5 48-48V304c0-26.5-21.5-48-48-48z"></path>
  </svg>
);

const PrevIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

const NextIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

// Composant pour texte caché visuellement mais accessible aux lecteurs d'écran
const VisuallyHidden = styled.span`
  position: absolute;
  width: 0.9px;
  height: 0.9px;
  padding: 0;
  margin: -0.9px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

const Testimonials = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef(null);

  const testimonials = [
    {
      id: "testimonial-decko",
      text: "Super expérience avec Rankeed ! L’équipe a été à l’écoute dès le début, très disponible tout au long du projet. La livraison a été rapide et le site est vraiment pensé pour les utilisateurs : fluide, simple et clair. Je recommande à 100 % !",
      name: "Olivier Diard",
      position: "Dirigeant, Société Decko",
      company: "Decko",
      image: "https://decko.fr/logo_decko_vecto.png",
      imageAlt: "Photo portrait de Olivier Diard",
      service: "Refonte de site vitrine"
    },
    {
      id: "testimonial-caroline",
      text: "La création de mon site web a été un vrai plaisir grâce à Rankeed. Ils ont su comprendre mes besoins et m’accompagner dans chaque étape. Le résultat est au-delà de mes attentes !",
      name: "Caroline Caumon",
      position: "Formatrice, Caroline Caumon",
      company: "Caroline Caumon",
      image: "https://carolinecaumonmassagemethode.com/images/profil.jpg",
      imageAlt: "Photo portrait de Caroline Caumon",
      service: "Création de site vitrine"
    },
  ];

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Détection préférence réduction de mouvement
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      setIsPaused(true);
    }

    const handleChange = (e) => {
      setIsPaused(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // Rotation automatique des témoignages
  useEffect(() => {
    let interval;

    if (!isPaused) {
      interval = setInterval(() => {
        nextTestimonial();
      }, 7000); // Temps augmenté pour une meilleure expérience utilisateur
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTestimonial, isPaused, testimonials.length]);

  // Mise en pause au hover/focus
  const pauseRotation = () => setIsPaused(true);
  const resumeRotation = () => setIsPaused(false);

  return (
    <TestimonialsSection id="testimonials" aria-labelledby="testimonials-title">
      <SectionTitle id="testimonials-title">Témoignages Clients</SectionTitle>
      <SectionSubtitle>
        Découvrez ce que nos clients pensent de notre travail de création de sites web et des résultats concrets obtenus pour leurs entreprises.
      </SectionSubtitle>

      <TestimonialsContainer>
        <TestimonialCarousel
          role="region"
          aria-roledescription="carrousel"
          aria-label="Témoignages clients"
          ref={carouselRef}
          onMouseEnter={pauseRotation}
          onMouseLeave={resumeRotation}
          onFocus={pauseRotation}
          onBlur={resumeRotation}
        >
          <TestimonialCard>
            <QuoteIcon><QuoteIconSvg /></QuoteIcon>
            <TestimonialText>
              {testimonials[activeTestimonial].text}
            </TestimonialText>
            <ClientInfo>
              <ClientImage
                src={testimonials[activeTestimonial].image}
                alt={testimonials[activeTestimonial].imageAlt}
                width="54"
                height="54"
              />
              <ClientDetails>
                <ClientName>{testimonials[activeTestimonial].name}</ClientName>
                <ClientPosition>
                  {testimonials[activeTestimonial].position}
                  <meta itemProp="reviewRating" content="5" />
                </ClientPosition>
              </ClientDetails>
            </ClientInfo>
          </TestimonialCard>

          <TestimonialControls aria-label="Contrôles du carrousel de témoignages">
            <ControlButton
              onClick={prevTestimonial}
              aria-label="Témoignage précédent"
              disabled={testimonials.length <= 1}
            >
              <PrevIcon />
            </ControlButton>

            <TestimonialDots>
              {testimonials.map((testimonial, index) => (
                <Dot
                  key={testimonial.id}
                  active={index === activeTestimonial}
                  onClick={() => setActiveTestimonial(index)}
                  aria-label={`Afficher le témoignage de ${testimonial.name}`}
                  aria-current={index === activeTestimonial ? "true" : "false"}
                >
                  <VisuallyHidden>
                    {testimonial.name}, {testimonial.company}
                  </VisuallyHidden>
                </Dot>
              ))}
            </TestimonialDots>

            <ControlButton
              onClick={nextTestimonial}
              aria-label="Témoignage suivant"
              disabled={testimonials.length <= 1}
            >
              <NextIcon />
            </ControlButton>
          </TestimonialControls>
        </TestimonialCarousel>
      </TestimonialsContainer>
    </TestimonialsSection>
  );
};

export default Testimonials;